import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { DeviceIconDisplay } from '../icons/DeviceIcons';
import { GroupIcon } from './ManageGroupsModal';
import { resolveGroupTextColor } from '../shared/groupColors';
import { getTimezoneLabel } from '../../utils/timezoneList';
import { getCameraRecordingState } from '../../utils/cameraRecordingState';
import { deviceDisplayName } from '../../utils/deviceName';
import { formatAssetLabel } from '../../utils/deviceLabel';
import { formatDT, convertSpeed, speedUnitLabel, formatTimeAgo } from '../../utils/timezone';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveTracking } from '../../contexts/LiveTrackingContext';
import cameraVideoIconUrl from '../../assets/camera-video.svg';
import {
  Search,
  MoreVertical,
  History as HistoryIcon,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Crosshair,
  Users,
  Pencil,
  ExternalLink,
  Share2,
  Link2,
  LinkIcon,
  StreetView,
  Gauge,
  Route,
  Layers,
  X,
  Bluetooth,
  Battery,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  Zap,
} from 'lucide-react';

// ── Color helpers ──────────────────────────────────────────
const darkenHex = (hex, amount = 0.15) => {
  if (!hex) return hex;
  const h = hex.replace('#', '');
  const r = Math.max(0, Math.round(parseInt(h.substring(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(h.substring(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(h.substring(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Convert light mode color to dark mode equivalent
const toDarkModeColor = (hex) => {
  if (!hex) return hex;
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  // Match Command Templates dark mode brightness (~30% of original, deeper tones)
  return `#${Math.round(r * 0.30).toString(16).padStart(2, '0')}${Math.round(g * 0.30).toString(16).padStart(2, '0')}${Math.round(b * 0.30).toString(16).padStart(2, '0')}`;
};

// ── Status indicator helpers ──────────────────────────────
// GPRS active = device is not offline (single source of truth from backend)
const isGPRSActive = (device) => device.status !== 'offline';

// Explicit GPS fix status (Digital Matter FID 0). Classifies the last reported
// GPS observation: 'fresh' (valid, not old, within the device's offline threshold),
// 'last_known' (old-fix bit set or older than the threshold), 'invalid' (latest
// observation explicitly reports no valid fix). Returns null when the device does
// not report explicit fix status (satellite-count protocols keep legacy behaviour).
const getGpsFixState = (device) => {
  const fix = device.last_gps_fix;
  if (!fix || typeof fix !== 'object') return null;
  if (!fix.fix_valid) return { state: 'invalid', fix };
  // Small clock skew (GPS ts can be ~1s ahead of record time) clamps to age 0
  const ageMs = fix.gps_timestamp ? Math.max(0, Date.now() - new Date(fix.gps_timestamp).getTime()) : null;
  const threshold = (device.offline_threshold_secs || 600) * 1000;
  if (fix.old_fix || ageMs == null || ageMs >= threshold) return { state: 'last_known', fix, ageMs };
  return { state: 'fresh', fix, ageMs };
};

// Tooltip / status text for the GPS indicator (fix-reporting devices)
const gpsFixStatusText = (device, gpsActive) => {
  const fs = getGpsFixState(device);
  if (!fs) {
    if ((device.protocol || '') === 'Digital Matter') return 'GPS status unknown';
    return gpsActive ? 'GPS Fix' : 'No GPS';
  }
  const { fix } = fs;
  const acc = fix.position_accuracy_m != null ? ` · Estimated accuracy: ${fix.position_accuracy_m} m` : '';
  const lastFix = fix.gps_timestamp ? ` · Last fix: ${formatDT(fix.gps_timestamp, { timezone: device.effective_timezone })}` : '';
  if (fs.state === 'fresh') return `GPS fix obtained${fix.fix_3d ? ' · 3D' : ''}${acc}${lastFix}`;
  if (fs.state === 'last_known') return `Last known GPS position · ${fix.gps_timestamp ? formatTimeAgo(fix.gps_timestamp) : 'age unknown'}${acc}${lastFix}`;
  return `No valid GPS fix reported${lastFix ? ` · Last known position retained${lastFix}` : ''}`;
};

// GPS active = has fresh GPS data within offline threshold AND satellites > 0
const isGPSActive = (device) => {
  if (!isGPRSActive(device)) return false;
  // Devices with explicit fix status (Digital Matter): flags + fix age decide
  const fixState = getGpsFixState(device);
  if (fixState) return fixState.state === 'fresh';
  // Check freshness of GPS data
  const gpsTime = device.last_gps_time || device.last_position?.timestamp;
  if (!gpsTime) return false;
  const threshold = (device.offline_threshold_secs || 600) * 1000;
  if ((Date.now() - new Date(gpsTime).getTime()) >= threshold) return false;
  // Prioritise last_satellites (fresh from WebSocket) over last_position.satellites (stale from DB)
  const sats = device.last_satellites ?? device.last_position?.satellites ?? 0;
  return sats > 0;
};

const getIgnitionState = (device) => {
  // 1. Check io_mappings for explicit ignition type
  const ignMap = device.io_mappings?.find(
    (m) => m.io_type?.toLowerCase() === 'ignition'
  );
  if (ignMap && device.last_io_elements) {
    const v = device.last_io_elements[ignMap.io_id];
    if (v !== undefined) return Number(v) > 0;
  }
  // 2. Fallback: common IO keys
  const io = device.last_io_elements;
  if (io) {
    if (io['239'] !== undefined) return Number(io['239']) > 0; // Teltonika
    if (io['ignition'] !== undefined) return Number(io['ignition']) > 0;
    if (io['din1'] !== undefined) return Number(io['din1']) > 0;
  }
  // 3. Fallback: device status implies ignition
  return device.status === 'idle' || device.status === 'moving';
};

// Trailers/equipment/buildings (non-vehicle fleet assets) — mirrors backend TRAILER/EQUIPMENT_ASSET_TYPES.
const NON_VEHICLE_ASSET_TYPES = new Set(['trailer', 'container', 'tool', 'equipment', 'pallet', 'cargo', 'temperature_zone', 'door', 'device', 'building', 'other']);
const hasIgnitionMapping = (device) => (device?.io_mappings || []).some((m) => m.io_type?.toLowerCase() === 'ignition');

// ── SVG Status Icons ──────────────────────────────────────
const GprsIcon = ({ active, isMobile = false }) => (
  <svg viewBox="0 0 24 24" className={isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'}>
    <path fillRule="evenodd" clipRule="evenodd" fill={active ? '#22c55e' : '#9ca3af'} d="M18.75 4.25C18.96 4.25 19.18 4.25 19.35 4.26C19.53 4.27 19.72 4.3 19.92 4.38C20.35 4.56 20.69 4.9 20.87 5.33C20.95 5.53 20.98 5.72 20.99 5.9C21 6.0699 21 6.25978 21 6.47962V6.48V17.52V17.5204C21 17.7402 21 17.9301 20.99 18.1C20.98 18.28 20.95 18.47 20.87 18.67C20.69 19.1 20.35 19.44 19.92 19.62C19.72 19.7 19.53 19.73 19.35 19.74C19.18 19.75 18.94 19.75 18.73 19.75C18.52 19.75 18.32 19.75 18.15 19.74C17.97 19.73 17.78 19.7 17.58 19.62C17.15 19.44 16.81 19.1 16.63 18.67C16.55 18.47 16.52 18.28 16.51 18.1C16.5 17.9301 16.5 17.7402 16.5 17.5204V17.52V6.48V6.47962C16.5 6.25978 16.5 6.0699 16.51 5.9C16.52 5.72 16.55 5.53 16.63 5.33C16.81 4.9 17.15 4.56 17.58 4.38C17.78 4.3 17.97 4.27 18.15 4.26C18.32 4.25 18.53 4.25 18.75 4.25ZM12.25 7.25C12.46 7.25 12.68 7.25 12.85 7.26C13.03 7.27 13.22 7.3 13.42 7.38C13.85 7.56 14.19 7.9 14.37 8.33C14.45 8.53 14.48 8.72 14.49 8.9C14.5 9.0699 14.5 9.25978 14.5 9.47962V9.48V17.52V17.5204C14.5 17.7402 14.5 17.9301 14.49 18.1C14.48 18.28 14.45 18.47 14.37 18.67C14.19 19.1 13.85 19.44 13.42 19.62C13.22 19.7 13.03 19.73 12.85 19.74C12.68 19.75 12.49 19.75 12.27 19.75C12.06 19.75 11.81 19.75 11.65 19.74C11.47 19.73 11.28 19.7 11.08 19.62C10.65 19.44 10.31 19.1 10.13 18.67C10.05 18.47 10.02 18.28 10.01 18.1C10 17.9301 10 17.7402 10 17.5204V17.52V9.48V9.47962C10 9.25978 10 9.0699 10.01 8.9C10.02 8.72 10.05 8.53 10.13 8.33C10.31 7.9 10.65 7.56 11.08 7.38C11.28 7.3 11.47 7.27 11.65 7.26C11.82 7.25 12.03 7.25 12.25 7.25ZM6.35001 11.26C6.18001 11.25 5.98 11.25 5.77 11.25C5.56 11.25 5.31999 11.25 5.14999 11.26C4.96999 11.27 4.78 11.3 4.58 11.38C4.15 11.56 3.81 11.9 3.63 12.33C3.55 12.53 3.51999 12.72 3.50999 12.9C3.5 13.0699 3.5 13.2598 3.5 13.4796V13.48V17.52V17.5204C3.5 17.7402 3.5 17.9301 3.50999 18.1C3.51999 18.28 3.55 18.47 3.63 18.67C3.81 19.1 4.15 19.44 4.58 19.62C4.78 19.7 4.96999 19.73 5.14999 19.74C5.31999 19.75 5.52 19.75 5.73 19.75C5.94 19.75 6.18001 19.75 6.35001 19.74C6.53001 19.73 6.72 19.7 6.92 19.62C7.35 19.44 7.69 19.1 7.87 18.67C7.95 18.47 7.98001 18.28 7.99001 18.1C8 17.9301 8 17.7402 8 17.5204V17.52V13.48V13.4796C8 13.2598 8 13.0699 7.99001 12.9C7.98001 12.72 7.95 12.53 7.87 12.33C7.69 11.9 7.35 11.56 6.92 11.38C6.72 11.3 6.53001 11.27 6.35001 11.26Z" />
  </svg>
);

const GpsIcon = ({ active, isMobile = false }) => (
  <svg
    viewBox="0 0 24 24"
    className={isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'}
  >
    <path fill={active ? '#3b82f6' : '#9ca3af'} d="M18.813,12.837 C19.42,13.445 19.95,13.974 20.295,14.483 C20.681,15.052 20.879,15.653 20.767,16.381 C20.66,17.078 20.299,17.699 19.724,18.113 C18.142,19.254 15.86,19.752 13.958,19.752 C11.138,19.752 8.381,18.61 6.386,16.615 C4.392,14.621 3.25,11.863 3.25,9.043 C3.25,7.139 3.746,4.862 4.888,3.277 C5.303,2.703 5.924,2.341 6.621,2.234 C7.349,2.122 7.95,2.32 8.519,2.707 C9.028,3.052 9.557,3.581 10.165,4.189 Z M19.251,1.25 C20.632,1.25 21.751,2.369 21.751,3.75 C21.751,5.131 20.632,6.25 19.251,6.25 C19.008,6.25 18.773,6.215 18.551,6.151 L16.826,10.848 L15.268,9.289 L16.08,7.078 L13.768,7.789 L12.168,6.19 C13.215,5.821 14.287,5.506 15.358,5.193 C15.888,5.037 16.419,4.881 16.946,4.719 C16.82,4.421 16.751,4.094 16.751,3.75 C16.751,2.369 17.87,1.25 19.251,1.25 Z M9.626,22.414 C9.487,22.625 9.252,22.752 9,22.752 L3,22.752 C2.748,22.752 2.513,22.625 2.374,22.414 C2.235,22.204 2.211,21.938 2.311,21.706 L4.061,17.622 C4.234,17.218 4.321,17.016 4.48,16.987 C4.639,16.959 4.796,17.124 5.111,17.455 C5.181,17.53 5.253,17.603 5.326,17.676 C6.344,18.694 7.533,19.523 8.838,20.126 C8.947,20.176 9.001,20.201 9.04,20.241 C9.079,20.281 9.101,20.334 9.147,20.441 L9.689,21.706 C9.789,21.938 9.765,22.204 9.626,22.414 Z" />
  </svg>
);

const IgnitionIcon = ({ active, isMobile = false }) => (
  <svg
    viewBox="0 0 24 24"
    className={isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'}
  >
    <path fill={active ? '#f97316' : '#9ca3af'} d="M15.5,1.75 C19.228,1.75 22.25,4.772 22.25,8.5 C22.25,12.228 19.228,15.25 15.5,15.25 C14.697,15.25 13.925,15.109 13.209,14.851 L11.03,17.03 C10.89,17.171 10.699,17.25 10.5,17.25 L8.75,17.25 L8.75,19 C8.75,19.414 8.414,19.75 8,19.75 L6.25,19.75 L6.25,21.5 C6.25,21.914 5.914,22.25 5.5,22.25 L2.5,22.25 C2.086,22.25 1.75,21.914 1.75,21.5 L1.75,18.5 C1.75,18.301 1.829,18.11 1.97,17.97 L9.149,10.791 C8.891,10.075 8.75,9.303 8.75,8.5 C8.75,4.772 11.772,1.75 15.5,1.75 Z M18.03,7.03 C18.323,6.737 18.323,6.263 18.03,5.97 C17.737,5.677 17.263,5.677 16.97,5.97 L15.97,6.97 C15.677,7.263 15.677,7.737 15.97,8.03 C16.263,8.323 16.737,8.323 17.03,8.03 Z" />
  </svg>
);

const VideoRecordingIcon = ({ active, clickable, onClick, testId, isMobile }) => (
  <button
    type="button"
    className={`${isMobile ? 'h-8 w-8 p-1.5' : 'p-0.5'} inline-flex items-center justify-center rounded ${clickable ? 'cursor-pointer hover:bg-muted' : 'cursor-default'} ${active ? 'text-red-500' : 'text-muted-foreground/40'}`}
    onClick={(e) => {
      e.stopPropagation();
      if (clickable) onClick?.();
    }}
    data-testid={testId}
    aria-label={active ? 'Open live video for recording cameras' : 'No camera recording'}
    title={active ? 'Camera recording — open live video' : 'No camera recording'}
  >
    <span
      className={`block ${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'}`}
      style={{
        backgroundColor: 'currentColor',
        WebkitMask: `url(${cameraVideoIconUrl}) center / contain no-repeat`,
        mask: `url(${cameraVideoIconUrl}) center / contain no-repeat`,
      }}
    />
  </button>
);

const hasVideoEnabled = (device) => {
  const value = device?.has_video;
  return value === true || value === 1 || String(value).toLowerCase() === 'true';
};

const isAssetBeaconDevice = (device) => device?.device_type === 'beacon' && (device?.beacon_purpose || 'asset') === 'asset';

const beaconStatusLabel = (device) => {
  if (!isAssetBeaconDevice(device)) return null;
  return device.beacon_observed_location ? 'Observed Asset Beacon' : 'Asset Beacon · Not Detected';
};

// Beacon battery icon — green/amber/red by level, grey when unreported or offline.
export const beaconBatteryInfo = (device, offline = false) => {
  const pct = device.beacon_battery_percent;
  const low = device.beacon_low_battery === true || (pct != null && pct < 20);
  let out;
  if (pct == null && !low) out = { Icon: Battery, cls: 'text-muted-foreground/50', label: 'Battery unknown' };
  else if (low) out = { Icon: BatteryLow, cls: 'text-red-500', label: pct != null ? `Battery LOW ~${Math.round(pct)}%` : 'Battery LOW' };
  else if (pct >= 60) out = { Icon: BatteryFull, cls: 'text-green-600', label: `Battery ~${Math.round(pct)}%` };
  else out = { Icon: BatteryMedium, cls: 'text-amber-500', label: `Battery ~${Math.round(pct)}%` };
  if (offline) out = { ...out, cls: 'text-muted-foreground/50' };
  return out;
};

// ── Timestamp formatter ───────────────────────────────────
// Uses the user's configured date/time format (Settings > General) via the shared
// timezone util — never hardcode the format. `timezone` is the device's effective
// timezone (formatDT falls back to the user's timezone when not provided).
const formatLastSeen = (ts, timezone) => {
  if (!ts) return 'No data';
  return formatDT(ts, { timezone, seconds: true });
};

// ── Single Device Card ────────────────────────────────────
const DeviceCard = ({
  device,
  isSelected,
  isFollowed,
  isHidden,
  onSelect,
  onToggleFollow,
  onToggleHide,
  onEditDevice,
  onFollowNewWindow,
  onSharePosition,
  onGenerateLink,
  onRevokeLinks,
  userTimezone,
  sharePositionEnabled,
  generateLinkEnabled,
  cardCustomSettings,
  cardCustomAllowed,
  trailEnabled,
  isTrailOn,
  onToggleDeviceTrail,
  isDark,
  deviceIdleMinutes,
  customIcons,
  isSpeeding,
  eventCardColor,
  testIdPrefix = '',
  isMobile = false,
  liveVideoEnabled = false,
  onOpenLiveVideo,
  now,
}) => {
  const navigate = useNavigate();
  const { isFeatureEnabled, isSubpageEnabled } = useFeatureFlags();
  // Same gating as the Fleet pages' Travel History buttons
  const hasTravelHistory = isFeatureEnabled('analytics_page') && isSubpageEnabled('analytics_history');
  const assetName = deviceDisplayName(device);
  const isOffline = device.status === 'offline';
  const isAssetBeacon = isAssetBeaconDevice(device);
  const gprsActive = isGPRSActive(device);
  const gpsActive = isGPSActive(device);
  // Fresh GPS: online AND last_gps_time within the device's offline threshold
  const gpsFresh = (() => {
    if (isOffline) return false;
    const gpsTime = device.last_gps_time || device.last_position?.timestamp;
    if (!gpsTime) return false;
    const threshold = (device.offline_threshold_secs || 600) * 1000;
    return (now - new Date(gpsTime).getTime()) < threshold;
  })();
  // When offline, force ignition to OFF to prevent stale/misleading display
  const ignOn = isOffline ? false : getIgnitionState(device);
  // Buildings never show a Key; non-vehicle assets without a mapped ignition IO show Power instead.
  const assetTypeLc = (device.asset_type || '').toLowerCase();
  const showPowerIcon = !isAssetBeacon && (assetTypeLc === 'building' || (NON_VEHICLE_ASSET_TYPES.has(assetTypeLc) && !hasIgnitionMapping(device)));
  const cameraRecordingState = getCameraRecordingState(device);
  const showVideoIcon = liveVideoEnabled && hasVideoEnabled(device);
  const hasCameraRecording = showVideoIcon && cameraRecordingState.hasAnyRecording;
  const lastSeen = device.last_seen || device.last_position?.server_timestamp;
  const personName = device.driver_name || device.staff_name || device.passenger_name;
  // Show dash when no fresh GPS data. Beacons: speed = the detecting tracker's
  // speed at the last scan; dash when the beacon is not currently observed.
  const displaySpeed = isAssetBeacon
    ? (!isOffline ? Math.round(convertSpeed(device.last_speed || 0)) : '-')
    : (gpsFresh ? Math.round(convertSpeed(device.last_speed || 0)) : '-');
  const beaconBattery = isAssetBeacon ? beaconBatteryInfo(device, isOffline) : null;
  // Effective timezone: backend computes this for override devices, else use user's timezone
  const effectiveTz = device.effective_timezone || userTimezone;
  const tzMatchesUser = effectiveTz === userTimezone;
  // Timezone area name to display (friendly label, e.g. "Lebanon (EET/EEST)")
  const tzAreaName = getTimezoneLabel(effectiveTz);

  // Card customization background color
  const getCardBg = () => {
    if (!cardCustomAllowed || !cardCustomSettings?.enabled) return '';
    // Check if the current mode is enabled
    if (isDark && !(cardCustomSettings.darkModeEnabled ?? true)) return '';
    if (!isDark && !(cardCustomSettings.lightModeEnabled ?? true)) return '';
    // Determine the base color for this status
    let baseColor = '';
    let darkBaseKey = '';
    if (isOffline) {
      baseColor = cardCustomSettings.offlineColor || '';
      darkBaseKey = 'darkOfflineColor';
    } else {
      const isMoving = device.status === 'moving' || (device.last_speed || 0) > 3;
      const isStopped = device.status === 'stopped' || (!isMoving && device.status !== 'offline');
      if (isMoving) {
        baseColor = cardCustomSettings.movingColor || '';
        darkBaseKey = 'darkMovingColor';
      } else if (isStopped && ignOn) {
        // Check ignition ON time condition
        if (cardCustomSettings.ignOnConditionEnabled) {
          const condMins = cardCustomSettings.ignOnConditionMinutes || 5;
          const idleMins = typeof deviceIdleMinutes === 'number' ? deviceIdleMinutes : null;
          if (idleMins !== null && idleMins >= condMins) {
            baseColor = cardCustomSettings.stoppedIgnOnColor || '';
            darkBaseKey = 'darkStoppedIgnOnColor';
          } else {
            // Condition not met — fall back to Stopped (Ign OFF) color
            baseColor = cardCustomSettings.stoppedIgnOffColor || '';
            darkBaseKey = 'darkStoppedIgnOffColor';
          }
        } else {
          baseColor = cardCustomSettings.stoppedIgnOnColor || '';
          darkBaseKey = 'darkStoppedIgnOnColor';
        }
      } else if (isStopped && !ignOn) {
        baseColor = cardCustomSettings.stoppedIgnOffColor || '';
        darkBaseKey = 'darkStoppedIgnOffColor';
      }
    }
    if (!baseColor && !isDark) return '';
    // Dark mode: use dedicated dark color defaults or custom
    if (isDark) {
      const DARK_DEFAULTS = {
        darkOfflineColor: '',
        darkStoppedIgnOffColor: '#264f34',
        darkStoppedIgnOnColor: '#4f4822',
        darkMovingColor: '#1e5236',
        darkSpeedAlertColor: '#5e2230',
      };
      if (cardCustomSettings.darkModeCustom) {
        return cardCustomSettings[darkBaseKey] || DARK_DEFAULTS[darkBaseKey] || toDarkModeColor(baseColor);
      }
      return DARK_DEFAULTS[darkBaseKey] || toDarkModeColor(baseColor);
    }
    return baseColor;
  };
  const cardBgColor = getCardBg();
  // Event Engine card color override — takes priority over normal card customization
  // Supports separate dark/light mode colors
  const isEventFlash = Boolean(eventCardColor?.flash);
  const effectiveCardBgColor = eventCardColor
    ? (isDark
        ? (eventCardColor.dark || toDarkModeColor(eventCardColor.light))
        : eventCardColor.light)
    : cardBgColor;

  // Card customization border stripe color
  const getCardBorder = () => {
    if (!cardCustomAllowed || !cardCustomSettings?.enabled || !cardCustomSettings?.borderEnabled) return null;
    if (isDark && !(cardCustomSettings.darkBorderEnabled ?? true)) return null;
    if (!isDark && !(cardCustomSettings.lightBorderEnabled ?? true)) return null;
    const BORDER_DEFS = {
      borderOfflineColor: '', borderStoppedIgnOffColor: '#a0ffbe',
      borderStoppedIgnOnColor: '#ffff82', borderMovingColor: '#34d399', borderSpeedAlertColor: '#ef4444',
    };
    const DARK_BORDER_DEFS = {
      darkBorderOfflineColor: '', darkBorderStoppedIgnOffColor: '#a0ffbe',
      darkBorderStoppedIgnOnColor: '#ffff82', darkBorderMovingColor: '#34d399', darkBorderSpeedAlertColor: '#f87171',
    };
    let lightKey = '', darkKey = '';
    if (isOffline) {
      lightKey = 'borderOfflineColor'; darkKey = 'darkBorderOfflineColor';
    } else {
      const isMoving = device.status === 'moving' || (device.last_speed || 0) > 3;
      const isStopped = device.status === 'stopped' || (!isMoving && device.status !== 'offline');
      if (isMoving) {
        lightKey = 'borderMovingColor'; darkKey = 'darkBorderMovingColor';
      } else if (isStopped && ignOn) {
        if (cardCustomSettings.ignOnConditionEnabled) {
          const condMins = cardCustomSettings.ignOnConditionMinutes || 5;
          const idleMins = typeof deviceIdleMinutes === 'number' ? deviceIdleMinutes : null;
          if (idleMins !== null && idleMins >= condMins) {
            lightKey = 'borderStoppedIgnOnColor'; darkKey = 'darkBorderStoppedIgnOnColor';
          } else {
            lightKey = 'borderStoppedIgnOffColor'; darkKey = 'darkBorderStoppedIgnOffColor';
          }
        } else {
          lightKey = 'borderStoppedIgnOnColor'; darkKey = 'darkBorderStoppedIgnOnColor';
        }
      } else if (isStopped) {
        lightKey = 'borderStoppedIgnOffColor'; darkKey = 'darkBorderStoppedIgnOffColor';
      }
    }
    if (!lightKey) return null;
    if (isDark) {
      if (cardCustomSettings.darkBorderCustom) {
        return (cardCustomSettings[darkKey] ?? DARK_BORDER_DEFS[darkKey]) || null;
      }
      return DARK_BORDER_DEFS[darkKey] || null;
    }
    return (cardCustomSettings[lightKey] ?? BORDER_DEFS[lightKey]) || null;
  };
  const cardBorderColor = getCardBorder();
  const tid = (id) => `${testIdPrefix}${id}`;

  return (
    <div
      className={`${isMobile ? 'px-3 py-2' : 'px-2 py-1'} cursor-pointer transition-all duration-200 ${isHidden ? 'opacity-50' : ''} ${isSelected ? 'relative z-[1]' : ''} ${isEventFlash ? 'animate-pulse' : ''}`}
      style={{
        borderLeft: cardBorderColor ? `${cardCustomSettings?.borderThickness || 4}px solid ${cardBorderColor}` : undefined,
        ...(effectiveCardBgColor ? { backgroundColor: effectiveCardBgColor } : {}),
        ...(isSelected ? {
          backgroundColor: effectiveCardBgColor || (isDark ? 'rgba(37, 99, 235, 0.18)' : 'rgba(219, 234, 254, 0.9)'),
          boxShadow: isDark
            ? 'inset 0 0 0 1px rgba(96,165,250,0.65), 0 6px 18px rgba(0,0,0,0.45)'
            : 'inset 0 0 0 1px rgba(37,99,235,0.45), 0 5px 16px rgba(37,99,235,0.18)',
          borderRadius: '6px',
        } : {}),
      }}
      onClick={() => {
        if (isMobile) document.activeElement?.blur?.();
        onSelect(device.id);
      }}
      data-testid={tid(`device-card-${device.id}`)}
    >
      <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-1'}`}>
        {/* Left: Follow + Hide buttons */}
        <div className={`flex flex-col ${isMobile ? 'gap-1' : 'gap-0.5'} flex-shrink-0`} onClick={(e) => e.stopPropagation()}>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`p-0.5 rounded ${isFollowed ? 'text-[#2563eb]' : 'text-muted-foreground/60 hover:text-foreground'}`}
                  onClick={() => onToggleFollow(device.id)}
                  data-testid={tid(`follow-btn-${device.id}`)}
                >
                  <Crosshair className={isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-[10px]">{isFollowed ? 'Stop following' : 'Follow device'}</TooltipContent>
            </Tooltip>
            {trailEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="flex items-center justify-center"
                    onClick={() => onToggleDeviceTrail?.(device.id)}
                    data-testid={tid(`trail-dot-${device.id}`)}
                  >
                    <span className={`block ${isMobile ? 'w-2.5 h-2.5' : 'w-2 h-2'} rounded-full transition-colors ${isTrailOn ? 'bg-[#2563eb]' : 'bg-muted-foreground/40'}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-[10px]">{isTrailOn ? 'Hide trail' : 'Show trail'}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`p-0.5 rounded ${isHidden ? 'text-red-400' : 'text-muted-foreground/60 hover:text-foreground'}`}
                  onClick={() => onToggleHide(device.id)}
                  data-testid={tid(`hide-btn-${device.id}`)}
                >
                  {isHidden ? <EyeOff className={isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} /> : <Eye className={isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-[10px]">{isHidden ? 'Show on map' : 'Hide from map'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Device Icon — bigger, tight padding */}
        <div className={`${isMobile ? 'w-10' : 'w-8'} flex-shrink-0 flex items-center justify-center text-muted-foreground`}>
          <DeviceIconDisplay
            iconName={device.device_icon || device.asset_type || 'car'}
            className={isMobile ? 'w-9 h-9' : 'w-8 h-8'}
            customIcons={customIcons}
            iconColor={device.device_icon_color}
          />
        </div>

        {/* Center: Name + Timestamp + Driver */}
        <div className="flex-1 min-w-0 ml-0.5">
          <span className={`${isMobile ? 'text-sm' : 'text-xs'} font-semibold truncate block leading-tight`} data-testid={tid(`device-name-${device.id}`)}>
            {assetName}
          </span>
          <span className={`${isMobile ? 'text-xs mt-0' : 'text-[10px] mt-0'} text-muted-foreground block truncate leading-tight`}>
            {formatLastSeen(lastSeen, effectiveTz)}
          </span>
          <span className={`${isMobile ? 'text-xs' : 'text-[10px]'} block truncate leading-tight ${tzMatchesUser ? 'text-blue-400' : 'text-amber-500'}`}>
            {tzAreaName}
          </span>
          {personName && (
            <span className={`${isMobile ? 'text-xs mt-0' : 'text-[10px] mt-0'} text-muted-foreground block truncate leading-tight`}>
              <Users className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />
              {personName}
            </span>
          )}
          {isAssetBeacon && (
            <span className={`${isMobile ? 'text-xs mt-0' : 'text-[10px] mt-0'} text-teal-700 dark:text-teal-300 block truncate leading-tight`} data-testid={tid(`beacon-observed-label-${device.id}`)}>
              <Bluetooth className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />
              {beaconStatusLabel(device)}{device.beacon_last_seen_by ? ` · ${device.beacon_last_seen_by}` : ''}
            </span>
          )}
        </div>

        {/* Right: Speed (top) + Status icons (bottom) */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {/* Speeding indicator — shown when device exceeds road speed limit */}
          {isSpeeding && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600 text-white animate-pulse" data-testid={tid(`speeding-badge-${device.id}`)}>
            <Gauge className="w-3 h-3" />
              <span className={`${isMobile ? 'text-[10px]' : 'text-[9px]'} font-bold uppercase tracking-wider`}>Speeding</span>
            </div>
          )}
          {/* Speed — top right, larger */}
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-0.5'}`}>
            <Gauge className={`${isMobile ? 'w-[18px] h-[18px]' : 'w-4 h-4'} ${isSpeeding ? 'text-red-500 animate-pulse' : device.status === 'moving' ? 'text-red-600' : 'text-muted-foreground'}`} />
            <span className={`${isMobile ? 'text-base' : 'text-sm'} font-bold tabular-nums ${isSpeeding ? 'text-red-500' : ''}`}>{displaySpeed}</span>
            <span className={`${isMobile ? 'text-xs' : 'text-[10px]'} text-muted-foreground`}>{speedUnitLabel()}</span>
          </div>
          {/* GPRS + GPS + Ignition + More — bottom right */}
          <div className="flex items-center gap-0.5">
            <TooltipProvider delayDuration={200}>
              {showVideoIcon && (
                <Tooltip><TooltipTrigger asChild><span>
                  <VideoRecordingIcon
                    active={hasCameraRecording}
                    clickable={hasCameraRecording}
                    onClick={() => onOpenLiveVideo?.(device)}
                    testId={tid(`device-video-${device.id}`)}
                    isMobile={isMobile}
                  />
                </span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{hasCameraRecording ? 'Camera Recording' : 'No Camera Recording'}</TooltipContent>
                </Tooltip>
              )}
              <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-gprs-${device.id}`)}><GprsIcon active={gprsActive} isMobile={isMobile} /></span></TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px]">{isAssetBeacon ? (gprsActive ? 'Observed · Online' : 'Not Detected · Offline') : (gprsActive ? 'GPRS Online' : 'GPRS Offline')}</TooltipContent>
              </Tooltip>
              {isAssetBeacon ? (
                <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-ble-${device.id}`)}><Bluetooth className={`${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} ${!isOffline ? 'text-teal-500' : 'text-muted-foreground/50'}`} /></span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{!isOffline ? 'Beacon Observed' : 'Beacon Not Detected'}</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-gps-${device.id}`)}><GpsIcon active={gpsActive} isMobile={isMobile} /></span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px] max-w-[260px]">{gpsFixStatusText(device, gpsActive)}</TooltipContent>
                </Tooltip>
              )}
              {isAssetBeacon ? (
                <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-battery-${device.id}`)}><beaconBattery.Icon className={`${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} ${beaconBattery.cls}`} /></span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{beaconBattery.label}</TooltipContent>
                </Tooltip>
              ) : showPowerIcon ? (
                <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-power-${device.id}`)}><Zap className={`${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} ${!isOffline ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/50'}`} /></span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{!isOffline ? 'Powered · Online' : 'Powered Off · Offline'}</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip><TooltipTrigger asChild><span className={isMobile ? 'inline-flex h-8 w-8 items-center justify-center' : ''} data-testid={tid(`device-ignition-${device.id}`)}><IgnitionIcon active={ignOn} isMobile={isMobile} /></span></TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{ignOn ? 'Ignition ON' : 'Ignition OFF'}</TooltipContent>
                </Tooltip>
              )}
              {device.active_tracking_links > 0 && (
                <Tooltip><TooltipTrigger asChild>
                  <span className={`relative flex items-center justify-center ${isMobile ? 'h-8 w-8' : ''}`} data-testid={tid(`device-links-${device.id}`)}>
                    <Link2 className={`${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} text-red-500`} />
                    <span className={`absolute ${isMobile ? 'top-0 right-0 w-3.5 h-3.5 text-[8px]' : '-top-1.5 -right-1.5 w-3 h-3 text-[7px]'} bg-red-500 text-white font-bold rounded-full flex items-center justify-center`}>
                      {device.active_tracking_links}
                    </span>
                  </span>
                </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">{device.active_tracking_links} active tracking link{device.active_tracking_links > 1 ? 's' : ''}</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`${isMobile ? 'h-8 w-8 p-1.5' : 'p-0.5'} inline-flex items-center justify-center rounded hover:bg-muted`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={tid(`device-more-${device.id}`)}
                >
                  <MoreVertical className={`${isMobile ? 'w-5 h-5' : 'w-3.5 h-3.5'} text-muted-foreground`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={isMobile ? 'w-64 p-1.5' : 'w-44'} data-testid={tid(`device-more-menu-${device.id}`)}>
                <DropdownMenuItem
                  disabled={device.asset_editing_enabled === false && device.icon_editing_enabled === false}
                  onClick={(e) => { e.stopPropagation(); onEditDevice?.(device); }}
                  data-testid={tid(`device-edit-${device.id}`)}
                  className={isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''}
                >
                  <Pencil className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Edit Device
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (device.last_latitude && device.last_longitude) {
                      window.open(
                        `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${device.last_latitude},${device.last_longitude}`,
                        '_blank'
                      );
                    }
                  }}
                  data-testid={tid(`device-street-view-${device.id}`)}
                  className={isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''}
                >
                  <ExternalLink className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Street View
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onFollowNewWindow?.(device.id); }}
                  data-testid={tid(`device-follow-window-${device.id}`)}
                  className={isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''}
                >
                  <ExternalLink className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Follow (New Window)
                </DropdownMenuItem>
                {hasTravelHistory && (
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); navigate(`/analytics?tab=history&device=${device.id}`); }}
                    data-testid={tid(`device-travel-history-${device.id}`)}
                    className={isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''}
                  >
                    <HistoryIcon className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Travel History
                  </DropdownMenuItem>
                )}
                {trailEnabled && (
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onToggleDeviceTrail?.(device.id); }}
                    data-testid={tid(`device-trail-toggle-${device.id}`)}
                    className={isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''}
                  >
                    <Route className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} />
                    {isTrailOn ? 'Hide Trail' : 'Show Trail'}
                    {isTrailOn && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#2563eb]" />}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={!sharePositionEnabled}
                  onClick={(e) => { e.stopPropagation(); if (sharePositionEnabled) onSharePosition?.(device); }}
                  data-testid={tid(`device-share-${device.id}`)}
                  className={`${isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''} ${!sharePositionEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!sharePositionEnabled ? 'Feature not available for your account' : ''}
                >
                  <Share2 className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Share Position
                  {!sharePositionEnabled && <span className={`ml-auto ${isMobile ? 'text-xs' : 'text-[10px]'} text-destructive`}>Restricted</span>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!generateLinkEnabled}
                  onClick={(e) => { e.stopPropagation(); if (generateLinkEnabled) onGenerateLink?.(device); }}
                  data-testid={tid(`device-generate-link-${device.id}`)}
                  className={`${isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''} ${!generateLinkEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={!generateLinkEnabled ? 'Feature not available for your account' : ''}
                >
                  <Link2 className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Generate Link
                  {!generateLinkEnabled && <span className={`ml-auto ${isMobile ? 'text-xs' : 'text-[10px]'} text-destructive`}>Restricted</span>}
                </DropdownMenuItem>
                {generateLinkEnabled && (
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onRevokeLinks?.(device); }}
                    className={`${isMobile ? 'min-h-[52px] text-sm px-3 py-3' : ''} text-red-600`}
                    data-testid={tid(`device-revoke-links-${device.id}`)}
                  >
                    <LinkIcon className={isMobile ? '!w-5 !h-5 mr-3' : 'w-3.5 h-3.5 mr-2'} /> Revoke All Links
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Group Header — canonical app-wide styling (matches Geofencing GroupSection).
// See /app/memory/groups-ui-pattern.md §2.7 for the shared style spec, §2.8 for the
// per-group visibility toggle pattern (Eye / EyeOff).
const GroupHeader = ({ name, count, color, isOpen, onToggle, onToggleVisibility, isHidden, testIdPrefix = '' }) => (
  <div
    className={`w-full sticky top-0 z-10 flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 ${isHidden ? 'opacity-60' : ''}`}
    data-testid={`${testIdPrefix}group-header-${name}`}
  >
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1.5 flex-1 min-w-0"
      aria-label={isOpen ? 'Collapse group' : 'Expand group'}
    >
      {isOpen ? <ChevronDown className="w-3 h-3 text-slate-700 dark:text-slate-200" /> : <ChevronRight className="w-3 h-3 text-slate-700 dark:text-slate-200" />}
      <Layers className="w-3 h-3" style={color ? { color: resolveGroupTextColor(color) } : undefined} />
      <span className="truncate" style={color ? { color: resolveGroupTextColor(color) } : { color: 'inherit' }}>{name}</span>
    </button>
    {onToggleVisibility && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(name); }}
        className={`shrink-0 p-0.5 rounded transition-colors ${isHidden ? 'text-amber-600 dark:text-amber-400 bg-white/60 dark:bg-slate-900/60' : 'text-slate-700 dark:text-slate-200 hover:text-[#2563eb] hover:bg-white/40 dark:hover:bg-slate-900/40'}`}
        aria-label={isHidden ? `Show ${name} on map` : `Hide ${name} from map`}
        title={isHidden ? `Show ${name} on map` : `Hide ${name} from map`}
        data-testid={`${testIdPrefix}group-visibility-${name}`}
      >
        {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
    )}
    <span className="text-[10px] font-normal text-slate-700 dark:text-slate-200">{count}</span>
  </div>
);

// ── Main Panel ────────────────────────────────────────────
export const DeviceListPanel = ({
  devices,
  selectedDevice,
  onDeviceSelect,
  followedDevices,
  hiddenDevices,
  onToggleFollow,
  onToggleHide,
  onFollowAll,
  onUnfollowAll,
  onHideAll,
  onShowAll,
  onEditDevice,
  onFollowNewWindow,
  onSharePosition,
  onGenerateLink,
  onRevokeLinks,
  userTimezone,
  sharePositionEnabled,
  generateLinkEnabled,
  cardCustomSettings,
  cardCustomAllowed,
  trailEnabled,
  trailEnabledDevices,
  onToggleDeviceTrail,
  onTrailAll,
  onUntrailAll,
  deviceIdleDurations,
  speedingDevices,
  eventCardColors,
  onManageGroups,
  hiddenGroupNames,
  onToggleGroupVisibility,
  liveVideoEnabled = false,
  onOpenLiveVideo,
  className = '',
  testId = 'device-list-panel',
  testIdPrefix = '',
  isMobile = false,
}) => {
  const { isDark } = useTheme();
  const { api } = useAuth();
  // Direct Consume from LiveTrackingContext so the device list filter survives
  // navigation away from /live-tracking (this child component unmounts whenever
  // the route changes).
  const {
    deviceListSearchQuery: searchQuery,
    setDeviceListSearchQuery: setSearchQuery,
    deviceListStatusFilter: statusFilter,
    setDeviceListStatusFilter: setStatusFilter,
    deviceListIsGrouped: isGrouped,
    setDeviceListIsGrouped: setIsGrouped,
    deviceListCollapsedGroups: collapsedGroups,
    setDeviceListCollapsedGroups: setCollapsedGroups,
  } = useLiveTracking();
  const tid = (id) => `${testIdPrefix}${id}`;
  const [customIcons, setCustomIcons] = useState([]);
  // Current time, refreshed every 15s — keeps the per-card GPS-freshness check pure at
  // render time instead of calling Date.now() directly in the DeviceCard render body.
  const [nowTs, setNowTs] = useState(() => Date.now());

  // Fetch custom icons once on mount
  useEffect(() => {
    if (api) {
      api.get('/custom-icons').then(res => setCustomIcons(res.data || [])).catch(() => {});
    }
  }, [api]);

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // Status counts — independent from telemetry panel status
  const statusCounts = useMemo(() => {
    const c = { all: devices.length, online: 0, moving: 0, idle: 0, stopped: 0, offline: 0, video: 0, recording: 0 };
    devices.forEach((d) => {
      if (d.status === 'moving' || d.status === 'idle' || d.status === 'stopped') c.online++;
      if (d.status === 'moving') c.moving++;
      if (d.status === 'idle') c.idle++;
      if (d.status === 'stopped') c.stopped++;
      if (d.status === 'offline') c.offline++;
      if (hasVideoEnabled(d)) c.video++;
      if (hasVideoEnabled(d) && getCameraRecordingState(d).hasAnyRecording) c.recording++;
    });
    return c;
  }, [devices]);

  // Filtered devices
  const filteredDevices = useMemo(() => {
    return devices.filter((d) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        (d.asset_name || '').toLowerCase().includes(q) ||
        (d.unit_number || '').toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q) ||
        (d.imei || '').includes(q) ||
        (d.registration || '').toLowerCase().includes(q) ||
        (d.driver_name || '').toLowerCase().includes(q) ||
        (d.staff_name || '').toLowerCase().includes(q) ||
        (d.passenger_name || '').toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'online' && (d.status === 'moving' || d.status === 'idle' || d.status === 'stopped')) ||
        (statusFilter === 'moving' && d.status === 'moving') ||
        (statusFilter === 'idle' && d.status === 'idle') ||
        (statusFilter === 'stopped' && d.status === 'stopped') ||
        (statusFilter === 'offline' && d.status === 'offline') ||
        (statusFilter === 'video' && hasVideoEnabled(d)) ||
        (statusFilter === 'recording' && hasVideoEnabled(d) && getCameraRecordingState(d).hasAnyRecording);
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      // iter151 Phase 2 — sort by the canonical "<Asset> / <Unit>"
      // label so the visible list order matches what the user sees.
      const nameA = formatAssetLabel(a).toLowerCase();
      const nameB = formatAssetLabel(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [devices, searchQuery, statusFilter]);

  // Grouped devices — track color + display_order per group via device.device_group_color/order
  const groupedDevices = useMemo(() => {
    if (!isGrouped) return null;
    const groups = {};
    const colorByGroup = {};
    const orderByGroup = {};
    filteredDevices.forEach((d) => {
      const g = d.device_group || 'Ungrouped';
      if (!groups[g]) groups[g] = [];
      groups[g].push(d);
      if (d.device_group_color && !colorByGroup[g]) colorByGroup[g] = d.device_group_color;
      if (typeof d.device_group_order === 'number' && orderByGroup[g] === undefined) orderByGroup[g] = d.device_group_order;
    });
    // Sort: by display_order ASC, then name ASC; Ungrouped is always last.
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Ungrouped') return 1;
      if (b === 'Ungrouped') return -1;
      const oa = orderByGroup[a] ?? 0;
      const ob = orderByGroup[b] ?? 0;
      if (oa !== ob) return oa - ob;
      return a.localeCompare(b);
    });
    return sorted.map(([name, list]) => [name, list, colorByGroup[name] || null]);
  }, [filteredDevices, isGrouped]);

  const toggleGroup = (name) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const statusFilters = [
    { key: 'all', label: 'All', count: statusCounts.all },
    { key: 'online', label: 'Online', count: statusCounts.online },
    { key: 'moving', label: 'Moving', count: statusCounts.moving },
    { key: 'idle', label: 'Idle', count: statusCounts.idle },
    { key: 'stopped', label: 'Stopped', count: statusCounts.stopped },
    { key: 'offline', label: 'Offline', count: statusCounts.offline },
    ...(liveVideoEnabled ? [{ key: 'video', label: 'Video', count: statusCounts.video }] : []),
    ...(liveVideoEnabled ? [{ key: 'recording', label: 'Recording', count: statusCounts.recording }] : []),
  ];

  const renderCard = (device) => (
    <DeviceCard
      key={device.id}
      device={device}
      isSelected={selectedDevice === device.id}
      isFollowed={followedDevices.has(device.id)}
      isHidden={hiddenDevices.has(device.id)}
      onSelect={onDeviceSelect}
      onToggleFollow={onToggleFollow}
      onToggleHide={onToggleHide}
      onEditDevice={onEditDevice}
      onFollowNewWindow={onFollowNewWindow}
      onSharePosition={onSharePosition}
      onGenerateLink={onGenerateLink}
      onRevokeLinks={onRevokeLinks}
      userTimezone={userTimezone}
      sharePositionEnabled={sharePositionEnabled}
      generateLinkEnabled={generateLinkEnabled}
      cardCustomSettings={cardCustomSettings}
      cardCustomAllowed={cardCustomAllowed}
      trailEnabled={trailEnabled}
      isTrailOn={trailEnabledDevices?.has(device.id) || false}
      onToggleDeviceTrail={onToggleDeviceTrail}
      isDark={isDark}
      deviceIdleMinutes={deviceIdleDurations?.get(device.id) ?? null}
      customIcons={customIcons}
      isSpeeding={!!speedingDevices?.[device.id]}
      eventCardColor={eventCardColors?.[device.id] || null}
      testIdPrefix={testIdPrefix}
      isMobile={isMobile}
      liveVideoEnabled={liveVideoEnabled}
      onOpenLiveVideo={onOpenLiveVideo}
      now={nowTs}
    />
  );

  return (
    <div
      className={`${className || 'w-72'} flex-shrink-0 flex flex-col bg-card rounded-lg border border-border overflow-hidden`}
      data-testid={testId}
    >
      {/* Search */}
      <div className={`${isMobile ? 'px-3 py-2' : 'px-2 py-1'} border-b border-border`}>
        <div className="relative">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-muted-foreground`} />
          <Input
            placeholder="Search devices, drivers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`${isMobile ? 'pl-9 pr-8 h-10 text-base' : 'pl-7 pr-7 h-7 text-xs'}`}
            data-testid={tid('device-search')}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid={tid('device-search-clear')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Status Filters (row 1) — dropdown menu (mobile + desktop) */}
      <div className={`${isMobile ? 'px-3 py-1.5' : 'px-2 py-1'} border-b border-border`}>
        {(() => {
          const activeFilter = statusFilters.find(f => f.key === statusFilter) || statusFilters[0];
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-muted dark:bg-[#162033] ${isMobile ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1.5 text-xs'} font-semibold uppercase tracking-wide text-foreground`}
                  data-testid={tid('filter-mobile-trigger')}
                >
                  <span className="truncate">{activeFilter.label} ({activeFilter.count})</span>
                  <ChevronDown className={`${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} shrink-0 text-muted-foreground`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[200px]" data-testid={tid('filter-mobile-menu')}>
                {statusFilters.map((f) => (
                  <DropdownMenuItem
                    key={f.key}
                    onSelect={() => setStatusFilter(f.key)}
                    className={`uppercase font-semibold tracking-wide text-xs ${statusFilter === f.key ? 'bg-[#2563eb] text-white focus:bg-[#2563eb] focus:text-white' : ''}`}
                    data-testid={tid(`filter-mobile-item-${f.key}`)}
                  >
                    {f.label} ({f.count})
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })()}
      </div>

      {/* Device action toggles (row 2) — centered, spread, larger */}
      <div className={`${isMobile ? 'px-3 py-1.5' : 'px-2 py-1'} border-b border-border`}>
        <div className="flex items-center justify-around">
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const allHidden = devices.length > 0 && devices.every(d => hiddenDevices.has(d.id));
                      if (allHidden) { onShowAll(); } else { onHideAll(); }
                    }}
                    data-testid={tid('hide-all-toggle')}
                    className={`p-2 rounded transition-colors ${
                      devices.length > 0 && devices.every(d => hiddenDevices.has(d.id))
                        ? 'bg-[#2563eb] text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-[#162033] dark:hover:bg-[#1e293b]'
                    }`}
                  >
                    {devices.length > 0 && devices.every(d => hiddenDevices.has(d.id))
                      ? <Eye className="w-4 h-4" />
                      : <EyeOff className="w-4 h-4" />
                    }
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px]">
                  {devices.length > 0 && devices.every(d => hiddenDevices.has(d.id)) ? 'Show all devices' : 'Hide all devices'}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const allFollowed = devices.length > 0 && devices.every(d => followedDevices.has(d.id));
                      if (allFollowed) { onUnfollowAll(); } else { onFollowAll(); }
                    }}
                    data-testid={tid('follow-all-toggle')}
                    className={`p-2 rounded transition-colors ${
                      devices.length > 0 && devices.every(d => followedDevices.has(d.id))
                        ? 'bg-[#2563eb] text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-[#162033] dark:hover:bg-[#1e293b]'
                    }`}
                  >
                    <Crosshair className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px]">
                  {devices.length > 0 && devices.every(d => followedDevices.has(d.id)) ? 'Unfollow all devices' : 'Follow all devices'}
                </TooltipContent>
              </Tooltip>
              {trailEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const allTrailed = devices.length > 0 && devices.every(d => trailEnabledDevices.has(d.id));
                      if (allTrailed) { onUntrailAll?.(); } else { onTrailAll?.(); }
                    }}
                    data-testid={tid('trail-all-toggle')}
                    className={`p-2 rounded transition-colors ${
                      devices.length > 0 && devices.every(d => trailEnabledDevices.has(d.id))
                        ? 'bg-[#2563eb] text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-[#162033] dark:hover:bg-[#1e293b]'
                    }`}
                  >
                    <Route className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px]">
                  {devices.length > 0 && devices.every(d => trailEnabledDevices.has(d.id)) ? 'Hide all trails' : 'Show all trails'}
                </TooltipContent>
              </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setIsGrouped((p) => !p)}
                    data-testid={tid('group-toggle')}
                    className={`p-2 rounded transition-colors ${
                      isGrouped
                        ? 'bg-[#2563eb] text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-[#162033] dark:hover:bg-[#1e293b]'
                    }`}
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[10px]">
                  {isGrouped ? 'Ungroup devices' : 'Group devices'}
                </TooltipContent>
              </Tooltip>
              {onManageGroups && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onManageGroups}
                      data-testid={tid('manage-groups-button')}
                      className="p-2 rounded transition-colors bg-muted text-muted-foreground hover:bg-[#2563eb] hover:text-white dark:bg-[#162033] dark:hover:bg-[#2563eb]"
                      aria-label="Manage device groups"
                    >
                      <GroupIcon className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[10px]">
                    Manage Groups
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
        </div>
      </div>

      {/* Device List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden" data-testid={tid('device-list-scroll')}>
        {filteredDevices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-xs">No devices found</p>
          </div>
        ) : isGrouped && groupedDevices ? (
          groupedDevices.map(([groupName, groupDevices, groupColor]) => (
            <div key={groupName}>
              <GroupHeader
                name={groupName}
                count={groupDevices.length}
                color={groupColor}
                isOpen={!collapsedGroups.has(groupName)}
                onToggle={() => toggleGroup(groupName)}
                onToggleVisibility={onToggleGroupVisibility}
                isHidden={hiddenGroupNames ? hiddenGroupNames.has(groupName) : false}
                testIdPrefix={testIdPrefix}
              />
              {!collapsedGroups.has(groupName) && (
                <div className="divide-y divide-border/50">
                  {groupDevices.map(renderCard)}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="divide-y divide-border/50">
            {filteredDevices.map(renderCard)}
          </div>
        )}
      </div>

      {/* Footer - Device count */}
      <div className={`${isMobile ? 'px-3 py-2' : 'px-2 py-1'} border-t border-border bg-muted/30`}>
        <span className={`${isMobile ? 'text-xs' : 'text-[10px]'} text-muted-foreground`} data-testid={tid('device-list-count')}>
          {filteredDevices.length} of {devices.length} devices
        </span>
      </div>
    </div>
  );
};
;
